import React from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import cn from 'classnames'

import Draft from 'inbox/draft'

import { ENTER, TAB } from 'constants/keys'

import AssignmentBadge from 'components/App/DesktopView/shared/AssignmentDropdown/Badge'
import AssignmentDropdown from 'components/App/DesktopView/shared/AssignmentDropdown/ReplyPage'
import { Flex, SVGIcon } from 'shared/ui'

import { isCmdEnter, stopEvent } from 'util/keys'
import { runOnNextTick } from 'util/functions'
import themeVars from 'ui_theme/site/globals/site.variables'
import withDropzone from 'components/Dropzone'

import AttachmentIcon from '../shared/AttachmentIcon'
import OverflowingDivider from '../shared/OverflowingDivider'
import NewToolbar from '../shared/NewToolbar'

import Editor from './Editor'
import Button from './Button'
import EmojiPicker from '../../EmojiPicker'
import styles from './styles.css'

import EditorDropzoneIndicator from '../shared/EditorDropzoneIndicator'
import { handleDrop } from '../shared/dragAndDrop'

// HACK (jscheel): No idea why we need this, but sui is not adding the margin
// here for some reason, even though it is on the reply form version.
const OverflowingDividerCSS = {
  marginTop: '1rem',
}

const FlexWithDropzone = withDropzone(Flex, {
  indicatorComponent: EditorDropzoneIndicator,
  className: cn(styles.EditorDropzone, 'Dropzone'),
})

const FooterWithDropzone = withDropzone(Flex, {
  skipIndicator: true,
})

class LogConversationEditor extends React.Component {
  static propTypes = {
    doUpdateAgentMention: PropTypes.func.isRequired,
    draft: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
    editorBottom: PropTypes.node,
    focusWhenInitialized: PropTypes.bool.isRequired,
    isDraftSendable: PropTypes.bool.isRequired,
    isMentioning: PropTypes.bool.isRequired,
    isOpen: PropTypes.bool, // is ticket open
    onSendStart: PropTypes.func,
    onSendEnd: PropTypes.func,
    onDeleteDraft: PropTypes.func, // The Log Conversation form shows this.
    updateDraftDelayed: PropTypes.func.isRequired,
    updateAndSendDraft: PropTypes.func.isRequired,
    ticketId: PropTypes.string.isRequired,
  }

  static defaultProps = {
    editorBottom: null,
    focusWhenInitialized: true,
    onDeleteDraft: undefined,
    onSendStart: undefined,
    onSendEnd: undefined,
    isOpen: true,
  }

  state = {
    word: null,
  }

  setStateAndSend = state => {
    return this.updateAndSend({ state })
  }

  getContent() {
    return this.editorRef && this.editorRef.getContent()
  }

  handleSend = () => {
    this.updateAndSend()
  }

  openAndSend = () => this.setStateAndSend('opened')

  closeAndSend = () => this.setStateAndSend('closed')

  snoozeAndSend = snoozeUntil => {
    return this.updateAndSend({ snoozeUntil, state: 'closed' })
  }

  updateAndSend = changes => {
    const { ticketId, updateAndSendDraft, onSendStart, onSendEnd } = this.props
    if (onSendStart) onSendStart()
    // Its possible that we have debounced body updates not yet applied.
    // So apply those immediately, then send the draft.
    const allChanges = {
      ...changes,
      body: this.getContent(),
    }

    return updateAndSendDraft(ticketId, allChanges).catch(() => {
      if (onSendEnd) onSendEnd()
    })
  }

  handleMention = mention => {
    const { doUpdateAgentMention } = this.props
    const found = mention

    if (!found) {
      if (this.state.word) this.setState({ word: null })
      return this.closeMentionModalUnlessClosed()
    }

    const word = found.word

    if (word.charAt(0) === '@') {
      this.setState({ word: found })
      return doUpdateAgentMention(found)
    }

    return this.closeMentionModalUnlessClosed()
  }

  closeMentionModalUnlessClosed = () => {
    if (!this.props.isMentioning) return
    this.props.doUpdateAgentMention(null)
  }

  handleEditorKeyDown = e => {
    // do not re-do the whole mention UI, we're going to dispatch a keydown event
    // so that the Mentions dropdown will capture it and behave like it did before
    // it's needed because tinymce events happen within an iframe
    const event = document.createEvent('Event')
    const type = e.type

    // can bubble, and is cancellable
    event.initEvent(`editor-${type}`, true, true)
    event.forwarded = e
    document.dispatchEvent(event)

    if ((e.keyCode === TAB || e.keyCode === ENTER) && this.state.word) {
      const {
        doMentionAgent,
        selectedMentionSelectorAgentId,
        suppressMentionSelector,
      } = this.props

      if (!suppressMentionSelector) {
        doMentionAgent(selectedMentionSelectorAgentId)
        e.preventDefault()
        e.stopPropagation()
      }
    }

    if (isCmdEnter(e)) {
      stopEvent(e)
      /**
       * pnagy: we need this here, because we need to let Tinymce to go through
       * its keyhandlers before we do our job here. Our job includes
       * navigation to the TICKET_PAGE and that will remove the editor before
       * it can finish
       * */
      runOnNextTick(this.closeAndSend)
    }
    return true
  }

  handleChange = (text, { firstEdit, deletedAll } = {}) => {
    const { updateDraftDelayed, ticketId } = this.props
    updateDraftDelayed(ticketId, 'body', text, { firstEdit, deletedAll })
  }

  handleDeleteDraft = () => {
    const { draft, onDeleteDraft, ticketId } = this.props

    if (!Draft.isEmpty(draft)) {
      const msg = 'Are you sure you want to delete this draft?'
      if (!window.confirm(msg)) return false // eslint-disable-line no-alert
    }

    const ref = this.editorRef

    if (ref) ref.setContent('')
    if (onDeleteDraft) onDeleteDraft(ticketId)
    if (ref) ref.focus()

    return true
  }

  saveEditorRef = node => (this.editorRef = node)

  /* Needed to ensure the editor rerenders when we change tickets */
  editorKey = () => {
    return `note-editor-ticket-${this.props.draft.ticketId}`
  }

  render() {
    const {
      className,
      config,
      draft,
      editorBottom,
      focusWhenInitialized,
      isDraftSendable,
      isOpen,
      onDeleteDraft,
    } = this.props

    return (
      <Flex
        className={cn(
          className,
          'LogConversationEditor grui fullHeight flex-col'
        )}
      >
        <FlexWithDropzone
          className="editorAndAttachments grui flex-col flex-2-1-auto"
          onDropCustom={handleDrop}
        >
          <Editor
            config={config}
            content={draft.body}
            focusWhenInitialized={focusWhenInitialized}
            forwardRef={this.saveEditorRef}
            key={this.editorKey()}
            onChange={this.handleChange}
            onKeyDown={this.handleEditorKeyDown}
            onMention={this.handleMention}
          />
          {editorBottom}
        </FlexWithDropzone>
        <OverflowingDivider horizontal css={OverflowingDividerCSS} />
        <FooterWithDropzone className="grui footer mt-4">
          <NewToolbar>
            <Flex>
              <AttachmentIcon forNote icon={<SVGIcon name="updatedAttach" />} />
            </Flex>
            {onDeleteDraft && (
              <SVGIcon
                data-test-id="DraftDeleteButton"
                name="updatedDelete"
                withTooltip={{
                  tooltip: 'Delete draft',
                  position: 'top',
                }}
                onClick={this.handleDeleteDraft}
              />
            )}
            <div className="grui ml-3">
              <EmojiPicker />
            </div>
          </NewToolbar>
          <Flex className="grui justify-end flex-1-0-auto">
            <div className="grui mr-3">
              <AssignmentDropdown
                direction="right"
                drilledDownGroupId={draft.assigned_group_id}
                key="assignment"
                offset={{ left: -16 }}
                trigger={
                  <div>
                    <AssignmentBadge
                      assignment={draft.assignment}
                      size="small"
                      iconSize={24}
                      tooltipPosition="top"
                    />
                  </div>
                }
                upward
              />
            </div>
            <div className="grui ml-4">
              <Button
                disabled={!isDraftSendable}
                isOpen={isOpen}
                onSend={this.handleSend}
                onSendClosedClick={this.closeAndSend}
                onSendOpenClick={this.openAndSend}
                onSnoozeOptionClick={this.snoozeAndSend}
              />
            </div>
          </Flex>
        </FooterWithDropzone>
      </Flex>
    )
  }
}

export default styled(LogConversationEditor)`
  width: 100%;
  height: 100%;
  background-color: ${props => props.theme.color.monochrome.white};
  padding: ${({ theme: { spacing } }) =>
    `${spacing['10']} ${spacing['10']} ${spacing['6']} ${spacing['10']}`};
  border: 1px solid ${props => props.theme.color.monochrome.medium};
  border-radius: ${themeVars.defaultBorderRadius};

  & .groove-inline-placeholder:before {
    opacity: 0.5;
  }

  .footer {
    flex: 0 0 auto;
  }
`
